import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Stack } from '@mui/material';
import { useNotifications } from 'modules/notification';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useOptimisticState } from 'hooks/useOptimistic';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';
import { Nullable, QueryListResponse, SelectOption } from 'global';
import { bpfAPI, BPFAPINameSpase, useBPFMutations } from 'modules/stages';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { BoardColumnEntity, MoveCardEventParams } from 'components/shared/Board/types';
import { dealPipeLineApi, initialVOFiltersValue, VOFilters } from 'modules/vendorOpportunity';

import { DealPipeLine } from './DealPipeLine';
import { DealPipelineListItem, DealPipelineResponse, DealPipelineVariables } from '../types';
import { generateBoardColumns, buildOptimisticColumns, mapVOFiltersToDealPipelineFilters } from '../lib';

interface Props {
  accountId: string;
  filters: Nullable<Partial<VOFilters>>;
}

export const DealPipelineCard = ({ accountId, filters = initialVOFiltersValue }: Props) => {
  const { handleCompleteStage, onUpdateDealCurrentStage } = useBPFMutations();
  const { showNotification } = useNotifications();

  const [selectedDepartment, setDepartment] = useState<Nullable<string>>(null);
  const [loadingCardId, setLoadingCardId] = useState<Nullable<string>>(null);
  const [columns, setColumns] = useOptimisticState<BoardColumnEntity<DealPipelineListItem>[]>([]);

  const serializedFilters = JSON.stringify(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mappedFilters = useMemo(() => mapVOFiltersToDealPipelineFilters(filters), [serializedFilters]);

  const [onFetchDealPipelineData, { data, previousData, loading: isDealInfoLoading }] = useLazyQuery<
    DealPipelineResponse,
    DealPipelineVariables
  >(dealPipeLineApi.fetchListWithSummary, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const fetchColumns = useCallback(
    async (departmentId: string) => {
      const result = await onFetchDealPipelineData({
        variables: { input: { accountId, filters: mappedFilters, departmentId } },
      });

      return generateBoardColumns(result.data?.dealPipeline?.entity ?? []);
    },
    [accountId, mappedFilters, onFetchDealPipelineData]
  );

  const {
    data: departmentsResponse,
    error: departmentsError,
    loading: isDepartmentsLoading,
  } = useQuery<BPFAPINameSpase<QueryListResponse<SelectOption>>>(bpfAPI.department.fetchDepartmentsOptions, {
    onCompleted({ bpf }) {
      if (!bpf?.result?.list?.length) {
        showNotification('Flows empty!', { type: 'info' });
        return;
      }

      const defaultDepartmentId = bpf.result?.list[0].value;
      setDepartment(defaultDepartmentId);
      setColumns(fetchColumns(defaultDepartmentId));
    },
  });

  useEffect(() => {
    if (selectedDepartment) {
      setColumns(fetchColumns(selectedDepartment));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedFilters]);

  const handleDepartmentChange = useCallback(
    async (departmentId: string) => {
      setDepartment(departmentId);
      setColumns(fetchColumns(departmentId));
    },
    [fetchColumns, setColumns]
  );

  const handleMoveCard = useCallback(
    (params: MoveCardEventParams) => {
      const { fromColumnId, itemIndex } = params;
      const cardEntity = columns.find(({ id }) => id === fromColumnId)?.cards[itemIndex];

      if (!cardEntity || !selectedDepartment) return;

      const optimisticColumns = buildOptimisticColumns(columns, params);

      setLoadingCardId(cardEntity.deal.id);

      const promise = (async () => {
        const { deal, stage } = cardEntity;

        const completeResult = await handleCompleteStage(stage.businessProcessFlowStageId, deal.id, BpfLinkType.Vo);

        if (!completeResult?.success || !completeResult?.data?.next?.businessProcessFlowStageId) {
          throw new Error('Stage completion failed');
        }

        await onUpdateDealCurrentStage({
          variables: {
            input: {
              parentId: deal.id,
              parentType: BpfLinkType.Vo,
              flowId: stage.businessProcessFlowId,
              nextStage: completeResult.data.next.businessProcessFlowStageId,
            },
          },
        });

        return fetchColumns(selectedDepartment);
      })();

      setColumns(promise, optimisticColumns)
        ?.catch(() => {})
        .finally(() => {
          setLoadingCardId(null);
        });
    },
    [
      columns,
      setColumns,
      fetchColumns,
      setLoadingCardId,
      selectedDepartment,
      handleCompleteStage,
      onUpdateDealCurrentStage,
    ]
  );

  const summary = (data ?? previousData)?.dealPipeline?.summary ?? null;
  const departments = departmentsResponse?.bpf?.result?.list ?? [];

  return (
    <RenderWithFallback
      error={departmentsError}
      isLoading={isDepartmentsLoading}
      fallBack={
        <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Stack>
      }
    >
      <DealPipeLine
        columns={columns}
        summary={summary}
        isStagesLoading={false}
        departments={departments}
        onMoveCard={handleMoveCard}
        loadingCardId={loadingCardId}
        isDepartmentsLoading={isDepartmentsLoading}
        onDepartmentChange={handleDepartmentChange}
        isDealInfoLoading={isDealInfoLoading && !previousData}
      />
    </RenderWithFallback>
  );
};
